/**
 * Zod schemas and parsers for structured note payloads.
 *
 * Used to validate LLM JSON responses and stored note strings before treating
 * them as structured data (invalid shapes fall back to plain text).
 */

import { z } from 'zod';

/** A single bullet-style item in Key Takeaways or Agreements / Decisions. */
export interface StructuredNoteTextItem {
  text: string;
}

/** One actionable next step, with optional owner and deadline when known. */
export interface StructuredNoteNextStep {
  task: string;
  /** Person responsible when explicitly mentioned; otherwise null. */
  responsible: string | null;
  /** Deadline when mentioned; otherwise null. */
  deadline: string | null;
}

/** The three AI / editable sections that make up a structured note. */
export interface StructuredNoteSections {
  keyTakeaways: StructuredNoteTextItem[];
  agreementsDecisions: StructuredNoteTextItem[];
  nextSteps: StructuredNoteNextStep[];
}

/**
 * Full persisted shape when AI structuring has been used.
 * Serialized with `JSON.stringify` into existing string note API fields.
 */
export interface StructuredNotePayload {
  originalNote: { content: string };
  structuredNote: StructuredNoteSections;
}

const textItemSchema = z.object({
  text: z.string(),
});

const nextStepSchema = z.object({
  task: z.string(),
  responsible: z.string().nullable(),
  deadline: z.string().nullable(),
});

/** Schema for the three structured sections (takeaways, agreements, next steps). */
export const structuredNoteSectionsSchema = z.object({
  keyTakeaways: z.array(textItemSchema),
  agreementsDecisions: z.array(textItemSchema),
  nextSteps: z.array(nextStepSchema),
});

/** Schema for the full persisted payload (original + structured). */
export const structuredNotePayloadSchema = z.object({
  originalNote: z.object({
    content: z.string(),
  }),
  structuredNote: structuredNoteSectionsSchema,
});

/**
 * Validates unknown data as structured sections.
 *
 * @param value - Typically a parsed LLM JSON response
 * @returns The sections, or `null` if validation fails
 */
export function parseStructuredNoteSections(value: unknown): StructuredNoteSections | null {
  const result = structuredNoteSectionsSchema.safeParse(value);
  return result.success ? result.data : null;
}

/**
 * Validates unknown data as a full structured note payload.
 *
 * @param value - Typically `JSON.parse` of a stored note string
 * @returns Zod safe-parse result for the caller to inspect
 */
export function parseStructuredNotePayload(value: unknown) {
  return structuredNotePayloadSchema.safeParse(value);
}
